import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '@shared/atoms/Button';
import Dialog from '@shared/molecules/Dialog';
import Icon from '@shared/atoms/Icon';
import LanguageSelect from '@shared/molecules/LanguageSelect';
import Select from '@shared/atoms/Select';
import T from '@shared/atoms/Typography';
import Tooltip from '@shared/atoms/Tooltip';
import VerticalTabs from '@shared/atoms/VerticalTabs';

import { langSelectIcons, newSelectTranslation, useForm } from '@shared/utils';

import theme from '@shared/themes/default';

import {
  COURSE_LEVELS,
  courseStatus,
  DOMAIN_LOCALES,
  MAX_COURSE,
} from '@shared/const';

import { useAppGlobals } from '../../../../contexts/AppContext';
import { TABS, useCourse } from '../../../../contexts/CourseContext';
import { hasAllEpisodesWithoutSubtitles } from '../../utils/episodeValidation';

import DescriptionDetails from './components/DescriptionDetails';
import ValidationList from '../../components/ValidationList';

const SectionStyled = styled.section`
  background: ${theme.palette.darkWrapper};
  padding: ${theme.spacing.s};
`;

const HeaderStyled = styled.header`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: ${theme.spacing.s};
`;

const LanguageWrapper = styled.div`
  align-items: center;
  display: flex;
  column-gap: ${theme.spacing.m1};
`;

const WrapperSelect = styled.div`
  display: flex;
  column-gap: ${theme.spacing.xl};
`;

const COURSE_LEVEL_NOT_SET = 'levelNoSet';

const cleanLocalisedArray = (arr) =>
  Array.isArray(arr) ? arr.filter((t) => t.value) : arr;

const normalizeLocalisedValue = (translation, languageCode) => {
  if (translation) {
    return {
      languageCode,
      value: translation.value ?? '',
    };
  }
  return { languageCode, value: '' };
};

const cleanOptionalTranslations = (arr) => {
  if (arr == null) {
    return null;
  }
  if (!Array.isArray(arr)) {
    return arr;
  }
  const cleaned = arr.filter(
    (t) => t?.value != null && String(t.value).trim() !== ''
  );
  return cleaned.length ? cleaned : null;
};

const Description = () => {
  const { t } = useTranslation();
  const { domain } = useAppGlobals();
  const languages = DOMAIN_LOCALES[domain];

  const {
    course,
    currentTabValidation,
    getLanguageStatus,
    locked,
    saveDescription,
    setCurrentTabValidation,
    setHandleSave,
    setHasChanged,
    submitting,
    validateCourse,
  } = useCourse();

  const [canPublish, setCanPublish] = useState(false);
  const [openLevelDialog, setOpenLevelDialog] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(languages[0]);
  const [valueChangeLevel, setValueChangeLevel] = useState();

  const isPublished = course?.status === courseStatus.PUBLISHED;

  const formInputFields = [
    {
      label: t('backoffice.courses.descriptionTab.name'),
      id: 'name',
      maxLength: MAX_COURSE.NAME_LENGTH,
      placeholder: t('backoffice.courses.descriptionTab.namePlaceholder'),
      required: true,
    },
    {
      label: t('backoffice.courses.descriptionTab.shortDescription'),
      id: 'shortDescription',
      maxLength: MAX_COURSE.SHORT_DESCRIPTION_LENGTH,
      minRows: 2,
      placeholder: t(
        'backoffice.courses.descriptionTab.shortDescriptionPlaceholder'
      ),
    },
    {
      label: t('backoffice.courses.descriptionTab.description'),
      id: 'description',
      maxLength: MAX_COURSE.DESCRIPTION_LENGTH,
      minRows: 4,
      placeholder: t(
        'backoffice.courses.descriptionTab.descriptionPlaceholder'
      ),
    },
  ];

  const handleSaveDescription = (values) => {
    const cleanLearningPoints = (arr) =>
      Array.isArray(arr) ? arr.filter((t) => t.values?.length > 0) : arr;

    saveDescription({
      ...values,
      name: cleanLocalisedArray(values.name),
      description: cleanLocalisedArray(values.description),
      shortDescription: cleanLocalisedArray(values.shortDescription),
      learningPoints: cleanLearningPoints(values.learningPoints),
      prerequisites: cleanOptionalTranslations(values.prerequisites),
      typicalLearner: cleanOptionalTranslations(values.typicalLearner),
      languages: { updateLanguages: [...languages] },
    });
  };

  const {
    changeValues,
    disableSubmit,
    edited,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    properties,
    setDefaults,
    setRequiredLocales,
    values,
    validate,
  } = useForm({
    fields: {
      ...formInputFields.reduce((acc, field) => {
        acc[field.id] = {
          localised: true,
          maxLength: field.maxLength,
          required: isPublished || field.required,
        };
        return acc;
      }, {}),
      learningPoints: {
        default: [],
        required: isPublished,
      },
      spokenLanguage: {
        default: DOMAIN_LOCALES[domain][0],
        required: true,
      },
      typicalLearner: {
        default: null,
        required: false,
      },
      prerequisites: {
        default: null,
        required: false,
      },
      level: {
        required: isPublished ? !!course.level : false,
      },
    },
    onSubmit: handleSaveDescription,
    locales: languages,
  });

  useEffect(() => {
    const { processedValues } = validate();

    const originalWithoutSubtitles = hasAllEpisodesWithoutSubtitles(
      course?.episodes
    );

    const validationStatus = validateCourse(
      { ...course, ...processedValues },
      languages,
      originalWithoutSubtitles
    );
    setCurrentTabValidation(validationStatus);
    const newCanPublish = Object.keys(validationStatus).length === 0;
    setCanPublish(newCanPublish);
  }, [values, languages]);

  useEffect(() => {
    if (course) {
      const padLocalised = (arr) =>
        languages.map((lang) =>
          normalizeLocalisedValue(
            (arr || []).find((t) => t.languageCode === lang),
            lang
          )
        );

      const sanitizeOptionalTranslations = (arr) => {
        if (!arr?.length) {
          return null;
        }
        const cleaned = arr
          .filter((t) => t?.value != null && String(t.value).trim() !== '')
          .map((t) => ({ languageCode: t.languageCode, value: t.value }));
        return cleaned.length ? cleaned : null;
      };

      setDefaults({
        ...formInputFields.reduce((acc, field) => {
          acc[field.id] = padLocalised(course[field.id]);
          return acc;
        }, {}),
        learningPoints: languages.map((lang) => {
          const existing = (course.learningPoints || []).find(
            (t) => t.languageCode === lang
          );
          return existing || { languageCode: lang, values: [] };
        }),
        spokenLanguage: course.spokenLanguage?.code || DOMAIN_LOCALES[domain],
        typicalLearner: sanitizeOptionalTranslations(course.typicalLearner),
        prerequisites: sanitizeOptionalTranslations(course.prerequisites),
        level: course.level,
      });
    }
  }, [course]);

  useEffect(() => {
    setHandleSave(() => handleSubmit);
  }, [values, edited]);

  useEffect(() => {
    setHasChanged(edited);
  }, [edited]);

  useEffect(() => {
    const usedLocales = languages.filter(
      (locale, index) =>
        index === 0 ||
        Object.entries(values).some(
          ([key, value]) =>
            key.endsWith(`#${locale}`) &&
            value != null &&
            String(value).trim() !== ''
        )
    );
    setRequiredLocales(usedLocales);
  }, [domain, languages, values]);

  const details = () => (
    <DescriptionDetails
      changeValues={changeValues}
      formErrors={errors}
      formInputFields={formInputFields}
      formProperties={properties}
      formValues={values}
      handleBlur={handleBlur}
      handleChange={handleChange}
      selectedLanguage={selectedLanguage}
    />
  );

  const verticalTabs = languages.map((language) => ({
    label: language,
    component: details(),
    status: getLanguageStatus(language, course),
    tabIcon: (
      <Icon svgContent={langSelectIcons[language]} viewBox="0 0 32 16" />
    ),
  }));

  const descriptionValidation = currentTabValidation?.[TABS.DESCRIPTION] || {};

  const [emptyTranslations, errorTranslations] = Object.keys(
    descriptionValidation
  ).reduce(
    ([accEmpty, accError], key) => {
      const hasError = !!Object.keys(descriptionValidation[key]).find(
        (value) =>
          ['prerequisites', 'typicalLearner'].includes(value) &&
          descriptionValidation[key][value] === true
      );

      const prerequisites = newSelectTranslation(
        course.prerequisites,
        selectedLanguage
      );
      const typicalLearner = newSelectTranslation(
        course.typicalLearner,
        selectedLanguage
      );

      const prerequisiteslang = course.prerequisites?.find(
        (item) => item.languageCode === selectedLanguage && item.value
      );

      const typicalLearnerlang = course.typicalLearner?.find(
        (item) => item.languageCode === selectedLanguage && item.value
      );

      const isEmpty =
        hasError &&
        prerequisiteslang &&
        typicalLearnerlang &&
        ((key in prerequisiteslang && !prerequisites) ||
          (key in typicalLearnerlang && !typicalLearner));

      return [accEmpty || isEmpty, accError || hasError];
    },
    [false, false]
  );

  const detailsSave = () => {
    return {
      [TABS.DESCRIPTION]: Object.keys(
        currentTabValidation?.[TABS.DESCRIPTION] || {}
      ).reduce((acc, key) => {
        const detailsLanguage = currentTabValidation[TABS.DESCRIPTION][key];
        const { prerequisites, typicalLearner } = detailsLanguage;
        return {
          ...acc,
          ...(prerequisites || typicalLearner
            ? {
                [key]: {
                  ...(prerequisites ? { prerequisites } : {}),
                  ...(typicalLearner ? { typicalLearner } : {}),
                },
              }
            : {}),
        };
      }, {}),
    };
  };

  const closeLevelChange = () => {
    setOpenLevelDialog(false);
    setValueChangeLevel();
  };

  const confirmLevelChange = (value = valueChangeLevel) => {
    changeValues({
      level: value,
      prerequisites: null,
      typicalLearner: null,
    });
    closeLevelChange();
  };

  const handleLevelChange = ({ value }) => {
    if (values.typicalLearner || values.prerequisites) {
      setValueChangeLevel(value);
      setOpenLevelDialog(true);
    } else {
      confirmLevelChange(value);
    }
  };

  const saveDisabled =
    disableSubmit ||
    (isPublished && (locked || !canPublish)) ||
    (isPublished && errorTranslations) ||
    (!isPublished && emptyTranslations);

  let tooltipTitle = '';
  let tooltipDetails = detailsSave();

  if (!isPublished && emptyTranslations) {
    tooltipTitle = t('backoffice.courses.saving.save');
  }

  if (
    course.status === courseStatus.PUBLISHED &&
    saveDisabled &&
    !submitting &&
    !locked &&
    Object.keys(currentTabValidation)?.length
  ) {
    tooltipTitle = t('backoffice.courses.publishing.save');
    tooltipDetails = currentTabValidation;
  }

  let courseLevels = Object.values(COURSE_LEVELS).map((level) => ({
    value: level,
    key: level,
    label: t(`course.levels.${level}.title`),
  }));

  if (!values.level) {
    courseLevels = [
      {
        value: COURSE_LEVEL_NOT_SET,
        key: COURSE_LEVEL_NOT_SET,
        label: t(`backoffice.courses.descriptionTab.${COURSE_LEVEL_NOT_SET}`),
      },
      ...courseLevels,
    ];
  }

  return (
    <>
      <Dialog
        actions={[
          {
            ariaLabel: t('generic.cancel'),
            handler: closeLevelChange,
            label: t('generic.cancel'),
            type: 'secondaryOutline',
          },
          {
            ariaLabel: t(
              'backoffice.courses.descriptionTab.changeLevel.changeLevel'
            ),
            handler: () => confirmLevelChange(),
            label: t(
              'backoffice.courses.descriptionTab.changeLevel.changeLevel'
            ),
          },
        ]}
        ariaLabel="level dialog"
        open={openLevelDialog}
        onClose={closeLevelChange}
        title={t('backoffice.courses.descriptionTab.changeLevel.title')}
      >
        <T color={theme.palette.pureDark} variant="paragraph">
          {t(`backoffice.courses.descriptionTab.changeLevel.text`)}
        </T>
      </Dialog>
      <SectionStyled>
        <HeaderStyled>
          <WrapperSelect>
            <LanguageWrapper>
              <T
                color={theme.palette.lightPure}
                fontWeight="semibold"
                variant="paragraph"
              >
                {t('backoffice.courses.descriptionTab.courseLanguage')}
              </T>
              <LanguageSelect
                disabled={(isPublished && locked) || submitting}
                languages={languages}
                onChange={({ value }) =>
                  handleChange({ name: 'spokenLanguage', value })
                }
                required
                showFlags={false}
                theme="dark"
                value={values.spokenLanguage}
              />
            </LanguageWrapper>
            <LanguageWrapper>
              <T
                color={theme.palette.lightPure}
                fontWeight="semibold"
                variant="paragraph"
              >
                {t('backoffice.courses.descriptionTab.levelLabel')}
              </T>
              <Select
                id="level"
                disabled={(isPublished && locked) || submitting}
                items={courseLevels}
                name="level"
                onChange={handleLevelChange}
                theme="dark"
                value={values.level || COURSE_LEVEL_NOT_SET}
              />
            </LanguageWrapper>
          </WrapperSelect>
          <Tooltip
            arrow
            title={
              tooltipTitle ? (
                <>
                  <T color={theme.palette.mainDark}>{tooltipTitle}</T>
                  <ValidationList details={tooltipDetails} />
                </>
              ) : (
                ''
              )
            }
          >
            <div>
              <Button
                ariaLabel={t('generic.save')}
                disabled={!edited || saveDisabled}
                loading={submitting}
                onClick={handleSubmit}
                type="secondary"
              >
                {t('generic.save')}
              </Button>
            </div>
          </Tooltip>
        </HeaderStyled>
        <nav>
          <VerticalTabs
            tabsToSelect={verticalTabs}
            setSelectedTab={(language) => setSelectedLanguage(language)}
          />
        </nav>
      </SectionStyled>
    </>
  );
};

export default Description;
